import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import { API_URL, AUTH_URL, CHAT_URL, OCR_URL } from "../../utils/constants";
import SendIcon from "@mui/icons-material/Send";
import { Snackbar } from "@mui/material";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { ThreeDotsLoader } from "../commonComponent/loader";
import MicIcon from "@mui/icons-material/Mic";
import SettingsVoiceIcon from "@mui/icons-material/SettingsVoice";
import BotHeader from "./BotHeader";
import picLogo from "../../assets/images/circular_logo.svg";
import botLogo from "../../assets/images/bot.png";
import micIconPng from "../../assets/images/Icon.png";
import uploadIconPng from "../../assets/images/Icon (1).png";
import sendButtonPng from "../../assets/images/Button.png";
import largeMicButtonPng from "../../assets/images/mic button.png";
import vectorPng from "../../assets/images/Vector.png";
import bgImage from "../../assets/images/background1.png";
import Avatar from "@mui/material/Avatar";
import Translation from "../commonComponent/Language/language";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import ScreenLoader from "../commonComponent/freezeScreenLoader";
import Swal from "sweetalert2";
import { useAuth } from "../../utils/AuthContext";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import { motion, AnimatePresence } from "framer-motion";
import voiceAssistantGif from "../../assets/images/voice_assis.gif";

// Helper: format seconds as m:ss
function fmtTime(secs) {
  if (!secs || isNaN(secs) || !isFinite(secs)) return "0:00";
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

// Custom lightweight audio player that displays correct dynamic duration
function AudioPlayer({ src, duration }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [totalDur, setTotalDur] = useState(duration || 0);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    if (duration > 0) setTotalDur(duration);
    
    const onMeta = () => {
      if (duration <= 0 && isFinite(el.duration)) setTotalDur(el.duration);
    };
    
    const onTime = () => {
      if (duration > 0 && isFinite(el.duration) && el.duration > 0) {
        // Map native progress to exact duration
        const ratio = el.currentTime / el.duration;
        setCurrent(ratio * duration);
      } else {
        setCurrent(el.currentTime);
      }
    };
    
    const onEnded = () => {
      setPlaying(false);
      setCurrent(totalDur);
    };
    
    el.addEventListener("loadedmetadata", onMeta);
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("ended", onEnded);
    return () => {
      el.removeEventListener("loadedmetadata", onMeta);
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("ended", onEnded);
    };
  }, [src, duration, totalDur]);

  const togglePlay = () => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) { 
      el.pause(); 
      setPlaying(false); 
    } else { 
      if (current >= totalDur) {
        el.currentTime = 0;
      }
      el.play().catch(() => {}); 
      setPlaying(true); 
    }
  };

  const handleSeek = (e) => {
    const el = audioRef.current;
    if (!el || !totalDur) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    
    if (duration > 0 && isFinite(el.duration) && el.duration > 0) {
      el.currentTime = ratio * el.duration;
    } else {
      el.currentTime = ratio * totalDur;
    }
    setCurrent(ratio * totalDur);
  };

  const progress = totalDur > 0 ? Math.min(100, Math.max(0, (current / totalDur) * 100)) : 0;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "6px", background: "#f3f4f6", borderRadius: "20px", padding: "4px 10px", minWidth: "220px" }}>
      <audio ref={audioRef} src={src} preload="metadata" style={{ display: "none" }} />
      <button
        onClick={togglePlay}
        style={{ background: "none", border: "none", cursor: "pointer", padding: "2px", fontSize: "14px", lineHeight: 1 }}
        title={playing ? "Pause" : "Play"}
      >
        {playing ? "⏸" : "▶"}
      </button>
      <span style={{ fontSize: "11px", color: "#555", minWidth: "30px", textAlign: "right" }}>{fmtTime(current)}</span>
      <div
        onClick={handleSeek}
        style={{ flex: 1, height: "4px", background: "#d1d5db", borderRadius: "2px", cursor: "pointer", position: "relative", overflow: "hidden" }}
      >
        <div style={{ width: `${progress}%`, height: "100%", background: "var(--brand-teal)", borderRadius: "2px" }} />
      </div>
      <span style={{ fontSize: "11px", color: "#555", minWidth: "30px" }}>{fmtTime(totalDur)}</span>
    </div>
  );
}

const generateUniqueNumber = () => {
  const min = 1000000000; // Smallest 10-digit number
  const max = 9999999999; // Largest 10-digit number
  return Math.floor(Math.random() * (max - min + 1)) + min;
};
const bars = [
  { height: "h-1", delay: 0 },
  { height: "h-2", delay: 0.2 },
  { height: "h-1", delay: 0.4 },
  { height: "h-2", delay: 0.5 },
  { height: "h-1", delay: 0.6 },
];

const GrievanceBot = ({ lang = null, setLang = null }) => {
  // const userId = user.userId;//sessionStorage.getItem("userId");
  const paragraphRef = useRef(null);
  const inputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioRef = useRef(null);
  const fileInputRef = useRef(null);
  const [copyBar, setCopyBar] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [chatbotInput, setChatbotInput] = useState([]);
  const [chatbotOutput, setChatbotOutput] = useState([]);
  const [isListening, setIsListening] = useState(false);
  const [audioBase64, setAudioBase64] = useState("");
  const [internalLang, setInternalLang] = useState(() => sessionStorage.getItem("appLang") || "en");
  const selectedLanguageCode = lang !== null ? lang : internalLang;
  const setSelectedLanguageCode = setLang !== null ? setLang : setInternalLang;
  const t = Translation[selectedLanguageCode]?.ui || Translation.en.ui;
  const [uniqueNumber, setUniqueNumber] = useState(generateUniqueNumber);
  const [textOutput, setTextOutput] = useState("");
  const [audioLoading, setAudioLoading] = useState(false);
  const [isListOpen, setIsListOpen] = useState(false);
  const [outputIndex, setOutputIndex] = useState(null);
  const [fileLoading, setFileLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [currentWord, setCurrentWord] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);
  const [fileContent, setFileContent] = useState("");
  const [feedback, setFeedback] = useState("");
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const { user } = useAuth();
  const recognitionRef = useRef(null);
  const submitTimeoutRef = useRef(null);
  const wantsToStopRef = useRef(false);
  const accumulatedTranscriptRef = useRef("");
  const currentSessionTranscriptRef = useRef("");
  const isSearchSubmittedRef = useRef(false);

  useEffect(() => {
    // The alert popup has been removed as per user request
  }, []);

  useEffect(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();

    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.onstart = () => {
      isSearchSubmittedRef.current = false;
      setIsListening(true);
    };

    // recognition.onend = () => {
    //   if (wantsToStopRef.current) {
    //     // User explicitly clicked stop or auto-submit fired — actually end
    //     setIsListening(false);
    //     wantsToStopRef.current = false;
    //     accumulatedTranscriptRef.current = "";
    //     currentSessionTranscriptRef.current = "";
    //   } else {
    //     // Browser killed it during a natural pause — save spoken text, then restart
    //     clearTimeout(submitTimeoutRef.current);
    //     if (currentSessionTranscriptRef.current.trim()) {
    //       accumulatedTranscriptRef.current = (
    //         accumulatedTranscriptRef.current + " " + currentSessionTranscriptRef.current
    //       ).trim();
    //       currentSessionTranscriptRef.current = "";
    //     }
    //     try {
    //       recognition.start();
    //     } catch (e) {
    //       setIsListening(false);
    //     }
    //   }
    // };

    // recognition.onresult = (event) => {
    //   if (isSearchSubmittedRef.current) return;
    //   let currentSessionText = "";

    //   for (let i = 0; i < event.results.length; i++) {
    //     currentSessionText += event.results[i][0].transcript;
    //   }

    //   currentSessionTranscriptRef.current = currentSessionText;

    //   // Combine past sessions + current session for full text
    //   const fullText = (
    //     accumulatedTranscriptRef.current + " " + currentSessionText
    //   ).trim();

    //   setSearchText(fullText);
    // };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognition.onresult = (event) => {
      if (isSearchSubmittedRef.current) return;
      let transcript = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }

      setSearchText(transcript);
      clearTimeout(submitTimeoutRef.current);

      submitTimeoutRef.current = setTimeout(() => {
        if (transcript.trim()) {
          isSearchSubmittedRef.current = true;
          handleSearch(transcript);
          recognition.abort();

        }
      }, 4000);
    };

    recognitionRef.current = recognition;
    return () => {
      clearTimeout(submitTimeoutRef.current);
      recognition.stop();
    };
  }, []);


  const handleMicClick = () => {
    const recognition = recognitionRef.current;
    if (!recognition) return;
    recognition.lang =
      selectedLanguageCode === "hi"
        ? "hi-IN"
        : "en-US";
    if (isListening) {
      wantsToStopRef.current = true;
      recognitionRef.current?.stop();
      // Submit the accumulated text when user clicks stop
      const fullText = (
        accumulatedTranscriptRef.current + " " + currentSessionTranscriptRef.current
      ).trim();
      if (fullText) {
        handleSearch(fullText);
      }
    } else {
      wantsToStopRef.current = false;
      accumulatedTranscriptRef.current = "";
      currentSessionTranscriptRef.current = "";
      setSearchText("");
      recognitionRef.current?.start();
    }
  };


  let audioChunks = [];

  const closePopup = () => setIsPopupOpen(false);

  const handleFeedbackChange = (e) => setFeedback(e.target.value);

  const handleLanuageClick = (code) => {
    setSelectedLanguageCode(code);
  };

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // setFileLoading(true);
    console.log("Selected file:", file.name);
    setFileContent(file);
    // const formData = new FormData();
    // formData.append("uploaded_file", file);

    // try {
    //   const response = await axios.post(
    //     `${API_URL}v1/upload_file`,
    //     formData,
    //     {
    //       headers: {
    //         "Content-Type": "multipart/form-data",
    //       },
    //     }
    //   );
    //   if (response?.data) {
    //     setTextOutput(response?.data.result);
    //   }
    //   console.log("Upload success:", response.data);
    // } catch (error) {
    //   console.error("Upload failed:", error);
    // } finally {
    //   setFileLoading(false);
    // }
    event.target.value = "";
  };

  const togglePopup = (index) => {
    setOutputIndex(index);
    setIsListOpen(!isListOpen);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  const regenerateNumber = () => {
    setUniqueNumber(generateUniqueNumber());
  };

  useEffect(() => {
    if (fileContent) {
      handleSearch();
    }
  }, [fileContent]);

  const handleSearch = async (input) => {
    clearTimeout(submitTimeoutRef.current);
    recognitionRef.current?.stop();
    const text = typeof input === "string" ? input : searchText;
    stopListening();
    let arr = chatbotInput;
    let inputText = "";

    if (fileContent) {
      inputText = `${fileContent.name} file has been uploaded`;
    } else if (text) {
      inputText = text;
    }

    arr.push(inputText || arr[arr.length - 1]);
    setChatbotInput(arr);
    setSearchText("");
    setShowSuggestions(false);
    setLoading(true);

    try {
      let config = {
        headers: {
          "X-Request-ID": user.userId,
          "x-source": uniqueNumber,
          "x-consumer-id": 1,
        },
      };

      let body;
      let endpoint;

      if (fileContent) {
        endpoint = "v1/upload_file";
        const formData = new FormData();
        formData.append(
          "uploaded_file",
          fileContent
        );
        formData.append(
          "input",
          JSON.stringify({
            language: selectedLanguageCode,
            text: "",
            audio: "",
            context: "JB_system_prompt",
          })
        );
        formData.append(
          "output",
          JSON.stringify({
            format: "text",
          })
        );

        config.headers["Content-Type"] = "multipart/form-data";
        body = formData;
      } else {
        // For text input only, use pure JSON body
        endpoint = "v1/jb_categorize_grievance";
        config.headers["Content-Type"] = "application/json";
        config.headers["USER-ID"] = user?.userId || "";
        body = {
          input: {
            language: selectedLanguageCode,
            text: text || arr[arr.length - 1],
            audio: "",
            context: "JB_system_prompt",
          },
          output: {
            format: "text",
          },
        };
      }

      const res = await axios.post(
        `${CHAT_URL}${endpoint}`,
        body,
        config
      );

      if (res?.data) {
        let outputArr = chatbotOutput;
        outputArr.push({
          ...res.data,
          isFileUpload: !!fileContent,
          selectedBucket: {
            ...res.data?.grievance_bucket?.bucket1,
            State: res.data?.state,
            Office_Area: res.data?.office_area,
            Department: "Ministry of Women and Child Development",
          },
        });

        if (res?.data.mandatory_data_present) {
          regenerateNumber();
        }

        setChatbotOutput(outputArr);
        if (fileContent) {
          // setTextOutput(res?.data?.response);
          setTextOutput("Test response");

        }
        else {

          setTextOutput(res?.data?.Regional_Response);
        }
        setLoading(false);
      } else {
        setLoading(false);
        let outputArr = chatbotOutput;
        outputArr.push({
          Regional_Response:
            Translation[selectedLanguageCode]?.apiErrorMessage ||
            "Sorry I cannot understand, please reframe the message.",
        });
        setChatbotOutput(outputArr);
      }
    } catch (error) {
      console.log("there is some error", error);
      setLoading(false);
      let outputArr = chatbotOutput;

      if (error.code === "ERR_NETWORK") {
        outputArr.push({
          Regional_Response:
            "The server is taking too long to respond. Please try again later.",
        });
      } else {
        outputArr.push({
          Regional_Response: "An error occurred while fetching data.",
        });
      }

      setChatbotOutput(outputArr);
    } finally {
      setFileContent("");
    }
  };


  // const handleSearch = async () => {
  //   stopListening();
  //   let arr = chatbotInput;
  //   let inputText = "";
  //   if (fileContent) {
  //     inputText = `${fileContent.name} file has been uploaded`;
  //   } else if (searchText) {
  //     inputText = searchText;
  //   }
  //   arr.push(inputText || arr[arr.length - 1]);
  //   setChatbotInput(arr);
  //   setSearchText("");
  //   setShowSuggestions(false);
  //   setLoading(true);
  //   try {
  //     const formData = new FormData();
  //     if (fileContent) {
  //       formData.append(
  //         "request_json",
  //         JSON.stringify({
  //           input: {
  //             language: selectedLanguageCode,
  //             text: "",
  //             audio: "",
  //             context: "JB_system_prompt",
  //           },
  //           output: {
  //             format: "text",
  //           },
  //         })
  //       );
  //       formData.append("file", fileContent);
  //     } else {
  //       formData.append(
  //         "request_json",
  //         JSON.stringify({
  //           input: {
  //             language: selectedLanguageCode,
  //             text: searchText || arr[arr.length - 1],
  //             audio: "",
  //             context: "JB_system_prompt",
  //           },
  //           output: {
  //             format: "text",
  //           },
  //         })
  //       );
  //     }

  //     // Make the POST request
  //     const res = await axios.post(
  //       `${API_URL}v1/jb_categorize_grievance`,
  //       formData, // Pass formData here
  //       {
  //         headers: {
  //           "Content-Type": "application/json",//"multipart/form-data", // Use multipart/form-data
  //           "X-Request-ID": userId,
  //           "x-source": uniqueNumber,
  //           "x-consumer-id": 1,
  //         },
  //       }
  //     );
  //     if (res?.data) {
  //       let outputArr = chatbotOutput;
  //       outputArr.push({
  //         ...res.data,
  //         selectedBucket: {
  //           ...res.data?.grievance_bucket?.bucket1,
  //           State: res.data?.State,
  //           District: res.data?.District,
  //           City: res.data?.City,
  //         },
  //       });
  //       if (res?.data.mandatory_data_present) {
  //         regenerateNumber();
  //       }
  //       setChatbotOutput(outputArr);
  //       setTextOutput(res?.data?.Regional_Response);
  //       setLoading(false);
  //     } else {
  //       setLoading(false);
  //       let outputArr = chatbotOutput;
  //       outputArr.push({
  //         Regional_Response:
  //           Translation[selectedLanguageCode]?.apiErrorMessage ||
  //           "Sorry I cannot understand, please reframe the message.",
  //       });
  //       setChatbotOutput(outputArr);
  //     }
  //   } catch (error) {
  //     console.log("there is some error", error);
  //     setLoading(false);
  //     let outputArr = chatbotOutput;
  //     if (error.code === "ERR_NETWORK") {
  //       outputArr.push({
  //         Regional_Response:
  //           "The server is taking too long to respond. Please try again later.",
  //       });
  //     } else {
  //       outputArr.push({
  //         Regional_Response: "An error occurred while fetching data.",
  //       });
  //     }
  //     setChatbotOutput(outputArr);
  //   } finally {
  //     setFileContent("");
  //   }
  // };

  useEffect(() => {
    const audioCall = async () => {
      setAudioLoading(true);
      try {
        let res = await axios.post(
          `${API_URL}process_audio`,
          {
            regional_answer: textOutput,
            language: selectedLanguageCode,
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        if (res?.data) {
          const base64Str = res?.data?.audio_base64;
          let audioUrl = "";
          let audioDuration = 0;
          if (base64Str) {
            try {
              const binary = atob(base64Str);
              const array = new Uint8Array(binary.length);
              for (let i = 0; i < binary.length; i++) {
                array[i] = binary.charCodeAt(i);
              }
              const blob = new Blob([array], { type: "audio/mp3" });
              audioUrl = URL.createObjectURL(blob);

              // Use AudioContext to decode and get EXACT duration
              try {
                const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
                const decoded = await audioCtx.decodeAudioData(array.buffer.slice(0));
                audioDuration = decoded.duration; // exact seconds
                audioCtx.close();
              } catch (decodeErr) {
                console.warn("Could not decode audio for duration:", decodeErr);
              }
            } catch (e) {
              audioUrl = `data:audio/mp3;base64,${base64Str}`;
            }
          }

          let outputArray = [...chatbotOutput];
          outputArray[outputArray.length - 1] = {
            ...outputArray[outputArray.length - 1],
            audio_base64: base64Str,
            audio_url: audioUrl,
            audio_duration: audioDuration,
          };
          setChatbotOutput(outputArray);
          setTextOutput("");

          // Removed auto-play based on user request.
        }
      } catch (error) {
        console.log("Error in generating audio", error);
      } finally {
        setAudioLoading(false);
      }
    };
    if (selectedLanguageCode && textOutput) {
      audioCall();
    }
  }, [textOutput, selectedLanguageCode]);

  const handleCopy = (text) => {
    navigator.clipboard?.writeText(text);
    setCopyBar(false);
  };

  const handleEdit = (text) => {
    inputRef.current?.focus();
    setSearchText(text);
  };

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setCopyBar(false);
  };

  const handleRegenerate = () => {
    handleSearch();
  };

  const startListening = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setSearchText("Microphone access requires a secure connection (HTTPS) or is not supported by your browser.");
        return;
      }
      setSearchText("Recording is in progress...");
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunks.push(event.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunks, { type: "audio/wav" });
        const reader = new FileReader();

        reader.onloadend = () => {
          const base64Audio = reader.result?.split(",")[1];
          setAudioBase64(base64Audio);
        };

        reader.readAsDataURL(audioBlob);
        audioChunks = []; // Clear the chunks for the next recording
      };

      mediaRecorderRef.current.start();
      setIsListening(true);
      console.log("Recording started...");
    } catch (error) {
      console.error("Error accessing the microphone:", error);
      setSearchText("Error accessing microphone. Please ensure permissions are granted.");
      setIsListening(false);
    }
  };

  const stopListening = () => {
    if (mediaRecorderRef.current) {
      setSearchText("Recording stopped. Please wait for a moment.");
      mediaRecorderRef.current.stop();
      setIsListening(false);
      console.log("Recording stopped.");
      // Clean up any remaining resources
      mediaRecorderRef.current.stream
        .getTracks()
        .forEach((track) => track.stop());
      mediaRecorderRef.current = null; // Clear reference
    }
  };

  useEffect(() => {
    const audioToText = async () => {
      try {
        let res = await axios.post(
          `${API_URL}speech2text`,
          {
            audio: audioBase64,
            source_language: selectedLanguageCode || "en",
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        if (res?.data) {
          console.log(res?.data, "line218");
          setSearchText(res?.data?.output);
        }
      } catch (error) {
        console.log("there is audio error", error);
      }
    };
    if (audioBase64) {
      audioToText();
    }
  }, [audioBase64]);

  useEffect(() => {
    return () => {
      window.speechSynthesis.cancel();
    };
  }, []);

  useEffect(() => {
    if (chatbotInput.length) {
      paragraphRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatbotInput.length]);

  const handleSuggestionClick = (text) => {
    setSearchText(text);
  };

  const fetchSuggestions = async (text) => {
    if (!text) {
      setSuggestions([]);
      return;
    }
    try {
      let res = await axios.post(
        `${API_URL}v1/transliterate`,
        {
          pipelineTasks: [
            {
              taskType: "transliteration",
              config: {
                language: {
                  sourceLanguage: "en",
                  targetLanguage: selectedLanguageCode,
                },
                serviceId: "ai4bharat/indicxlit--cpu-fsv2",
                isSentence: false,
                numSuggestions: 4,
              },
            },
          ],
          inputData: {
            input: [
              {
                source: text,
              },
            ],
          },
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      if (res?.data) {
        console.log(res?.data, "line218");
        let resultText = res.data?.pipelineResponse[0]?.output[0]?.target;
        setSuggestions(resultText);
      }
    } catch (error) {
      console.log("there is transliteration error", error);
    }
  };

  useEffect(() => {
    if (!currentWord) {
      setShowSuggestions(false);
      return;
    }
    if (selectedLanguageCode !== "en") {
      const debounce = setTimeout(() => {
        fetchSuggestions(currentWord);
      }, 500); // Debounce for 300ms

      return () => clearTimeout(debounce);
    }
  }, [currentWord]);

  const handleInputChange = (e) => {
    clearTimeout(submitTimeoutRef.current);
    recognitionRef.current?.stop();
    const value = e.target.value;
    setSearchText(value);

    // Extract the current word being typed
    const words = value?.split(" ");
    const lastWord = words[words.length - 1];
    setCurrentWord(lastWord);
    setShowSuggestions(true);
  };

  const handleTranslationClick = (suggestion) => {
    const words = searchText?.split(" ");
    words[words.length - 1] = suggestion; // Replace the current word with the suggestion
    const updatedText = words.join(" ");
    setSearchText(updatedText); // Update the search bar text
    setCurrentWord(""); // Clear the current word
    setShowSuggestions(false);
  };

  const handleSelectedBucket = (value) => {
    let outputArray = chatbotOutput;
    outputArray[outputIndex].selectedBucket = {
      ...outputArray[outputIndex].selectedBucket,
      ...value,
    };
    setChatbotOutput(outputArray);
    setIsListOpen(!isListOpen);
  };

  const handleLikeDislike = (i, value) => {
    const updatedOutputArray = [...chatbotOutput];
    updatedOutputArray[i] = { ...updatedOutputArray[i], userFeedback: value };
    setChatbotOutput(updatedOutputArray);
    if (value === "Disliked") {
      setIsPopupOpen(true);
    }
  };

  const handleSubmit = async (index) => {
    setSubmitLoading(true);
    console.log("chatbotOutput", chatbotOutput[index]);
    // Use the full, cleaned grievance produced by the bot as the stored
    // grievance content (instead of just the very first chat message), and
    // derive a short, meaningful title from that content.
    const deriveGrievanceTitle = (text) => {
      const clean = String(text || "").trim();
      if (!clean) return "";
      const firstSentence = clean.split(/(?<=[.!?])\s+/)[0] || clean;
      return firstSentence.length > 90
        ? firstSentence.slice(0, 90).trimEnd() + "..."
        : firstSentence;
    };
    const grievanceContent =
      chatbotOutput[index]?.improved_grievance ||
      chatbotOutput[index]?.summary ||
      chatbotInput[0] ||
      "";
    const grievanceTitle = deriveGrievanceTitle(grievanceContent);
    try {
      let res = await axios.post(
        `${AUTH_URL}api/create_query`,
        {
          Session_id: uniqueNumber,
          user_id: user.userId,
          user_query: grievanceContent,
          grievance_title: grievanceTitle,
          user_query_language_code: selectedLanguageCode,
          user_query_english: grievanceContent,
          llm_response_desc: chatbotOutput[index]?.Regional_Response,
          llm_ministry: chatbotOutput[index]?.selectedBucket?.Ministry || "",
          llm_category: chatbotOutput[index]?.selectedBucket?.Category,
          llm_subcategory:
            chatbotOutput[index]?.selectedBucket?.["Sub-Category"],
          severity: chatbotOutput[index]?.priority || 'High', // need to be update
          tat: chatbotOutput[index]?.tat || '', // need to be update
          user_feedback: chatbotOutput[index]?.userFeedback || "",
          city: chatbotOutput[index]?.selectedBucket?.office_area || '',
          district: chatbotOutput[index]?.selectedBucket?.District || chatbotOutput[index]?.district || '',
          state: chatbotOutput[index]?.selectedBucket?.state || chatbotOutput[index]?.state || '',
          query_feedbacktext: feedback || "",
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      if (res.status === 201) {
        setChatbotInput([]);
        setChatbotOutput([]);
        setFeedback("");
        Swal.fire({
          title: selectedLanguageCode === "hi" ? "शिकायत सफलतापूर्वक जमा की गई!" : "Complaint Submitted Successfully!",
          text: (selectedLanguageCode === "hi" ? "शिकायत आईडी : " : "Complaint ID : ") + res.data?.grievance_id,
          icon: "success",
          footer: `<a href="/desk">${t.dashboard || "Go to Dashboard"}</a>`,
        });
      }
    } catch (error) {
      console.log("Error in Create Query API", error);
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Error in Create Query API",
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <>
      <div className="flex-1 min-w-0 h-full relative flex flex-col shadow-md bg-[#ecf0fb] ">
        <Snackbar
          open={copyBar}
          autoHideDuration={5000}
          onClose={handleClose}
          message="Text copied"
          sx={{ width: "100%" }}
        />
        <BotHeader handleLanuageClick={handleLanuageClick} information={Translation[selectedLanguageCode]?.botScroller || "This assistant provides a simple way to submit complaints and track status of previously submitted requests"} />

        <div className={`relative w-full flex-1 z-0 ${chatbotInput?.length > 0 ? 'overflow-y-auto' : 'overflow-hidden'} bg-gradient-to-br from-[#ffffff] via-[#f7eef0] to-[#fbeef1]`}>
          <div className="absolute inset-0 pointer-events-none overflow-hidden z-0" aria-hidden="true">
            <svg className="wave-back absolute bottom-0 w-full h-[220px]" viewBox="0 0 2400 220" preserveAspectRatio="none">
              <path fill="#9c2942" fillOpacity="0.06" d="M0,180 Q100,60 200,180 Q300,60 400,180 Q500,60 600,180 Q700,60 800,180 Q900,60 1000,180 Q1100,60 1200,180 Q1300,60 1400,180 Q1500,60 1600,180 Q1700,60 1800,180 Q1900,60 2000,180 Q2100,60 2200,180 Q2300,60 2400,180 L2400,220 L0,220 Z" />
            </svg>
            <svg className="wave-front absolute bottom-0 w-full h-[180px]" viewBox="0 0 2400 180" preserveAspectRatio="none">
              <path fill="#4a0e1e" fillOpacity="0.045" d="M0,140 Q150,40 300,140 Q450,40 600,140 Q750,40 900,140 Q1050,40 1200,140 Q1350,40 1500,140 Q1650,40 1800,140 Q1950,40 2100,140 Q2250,40 2400,140 L2400,180 L0,180 Z" />
            </svg>
          </div>
          <div className="relative z-10 min-h-full pb-36">
            {chatbotInput?.length === 0 && (
              <div className="flex flex-col items-center mt-12 h-full py-2 gap-4">
                {/* Header Section */}
                <div className="text-center flex flex-col items-center justify-center gap-6">
                  <div 
                    className="w-[110px] h-[110px] rounded-full bg-white border-[5px] border-[#c1671f] flex items-center justify-center text-[#4c6478] shadow-[0_4px_10px_rgba(0,0,0,0.06)] cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={handleMicClick}
                    title="Tap to speak"
                  >
                    {isListening ? (
                      <motion.div
                        animate={{ scale: [1, 1.1, 1] }}
                        transition={{ duration: 0.6, repeat: Infinity }}
                        className="flex items-center justify-center w-full h-full"
                      >
                        <img src={voiceAssistantGif} alt="Recording" className="w-[84px] h-[84px] object-contain rounded-full" />
                      </motion.div>
                    ) : (
                       <i className="fa-solid fa-microphone text-[34px]"></i>
                    )}
                  </div>
                  <h3 className="text-[20px] text-[#0e2f4d] font-[800]">
                    {Translation[selectedLanguageCode]?.title ||
                      "NCW Sahayika — Your Digital AI Assistant"}
                  </h3>
                </div>

                {/* Suggested Questions Section */}
                <div className="flex justify-center items-center w-[95%] sm:w-[80%] max-w-[560px] mt-[16px]">
                  <div className="bg-white border-[1.5px] border-dashed border-[#e8d3d8] rounded-[14px] p-[18px_22px] min-h-[56px] shadow-[0_2px_12px_rgba(74,14,30,0.04)] relative flex items-center w-full cursor-pointer hover:border-[#9c2942] transition-colors">
                    <span className="absolute -top-[11px] left-[16px] bg-[#9c2942] text-white text-[10.5px] font-[700] tracking-[0.4px] py-[3px] px-[10px] rounded-full uppercase">
                      {Translation[selectedLanguageCode]?.suggestedQuestionsText || "TRY ASKING"}
                    </span>
                    <Swiper
                      modules={[Autoplay]}
                      autoplay={{
                        delay: 5000,
                        disableOnInteraction: false,
                      }}
                      loop
                      className="w-full h-full"
                    >
                    {Translation[selectedLanguageCode]
                      ?.suggestedQuestions.map((question, index) => (
                        <SwiperSlide key={index}>
                          <div className="flex items-center justify-center h-full cursor-pointer">
                            <p className="text-sm text-gray-800 text-center"
                              onClick={() =>
                                handleSuggestionClick(
                                  question
                                )
                              }>
                              {question}
                            </p>
                          </div>
                        </SwiperSlide>
                      ))}
                    </Swiper>
                  </div>
                </div>
              </div>
            )}
            {chatbotInput?.length > 0 &&
              chatbotInput.map((item, index) => {
                return (
                  <>
                    <div
                      key={index}
                      className="flex justify-end gap-x-3 mx-5 my-5 "
                      ref={paragraphRef}
                    >
                      <div className="border border-[#b5677e] shadow-[0_8px_10px_-6px_rgba(156,41,66,0.1),_0_20px_25px_-5px_rgba(156,41,66,0.1),_0_0_0_2px_rgba(156,41,66,0.3)] bg-[#faf1f2] text-black rounded-l-lg rounded-br-lg px-5 py-1 max-w-[90%]">
                        <div className="flex gap-3 my-1 text-black flex-1">
                          <div className="leading-relaxed">
                            <span className="text-[#9c2942] text-sm font-medium">
                              {item}
                            </span>

                            <div className="mt-2 flex gap-x-1 ">
                              <CopyToClipboard
                                text={item}
                                onCopy={() => handleCopy(item)}
                              >
                                <svg
                                  className="h-5 w-5 cursor-pointer"
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  strokeWidth="2"
                                    stroke="#9c2942"
                                  fill="none"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  {" "}
                                  <path stroke="none" d="M0 0h24v24H0z" />{" "}
                                  <rect
                                    x="8"
                                    y="8"
                                    width="12"
                                    height="12"
                                    rx="2"
                                  />{" "}
                                  <path d="M16 8v-2a2 2 0 0 0 -2 -2h-8a2 2 0 0 0 -2 2v8a2 2 0 0 0 2 2h2" />
                                </svg>
                              </CopyToClipboard>
                              <svg
                                className="h-5 w-5 cursor-pointer"
                                viewBox="0 0 24 24"
                                strokeWidth="2"
                                stroke="#9c2942"
                                fill="none"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                onClick={() => handleEdit(item)}
                              >
                                {" "}
                                <path stroke="none" d="M0 0h24v24H0z" />{" "}
                                <path d="M9 7 h-3a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-3" />{" "}
                                <path d="M9 15h3l8.5 -8.5a1.5 1.5 0 0 0 -3 -3l-8.5 8.5v3" />{" "}
                                <line x1="16" y1="5" x2="19" y2="8" />
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="relative flex shrink-0 items-center justify-center rounded-[16px] w-[44px] h-[44px] bg-gradient-to-br from-[#9c2942] to-[#4a0e1e]">
                        <img
                          src={picLogo}
                          alt="pic logo"
                          className="w-[24px] h-[24px] invert"
                        />
                      </div>
                    </div>
                    {chatbotOutput?.length > 0 && chatbotOutput[index] && (
                      <div className="flex gap-x-3 text-left mx-3 sm:mx-5 my-5 max-w-[95%] sm:max-w-[80%] ">
                        <div className="relative flex shrink-0 items-center justify-center rounded-[16px] w-[44px] h-[44px] border-[2px] border-white bg-gradient-to-br from-[#9c2942] to-[#4a0e1e]">
                          <img
                            src={botLogo}
                            alt="bot logo"
                            className="w-[24px] h-[24px] invert"
                          />
                        </div>
                        <div className="border-[1.6px] border-[#FFD6A8] shadow-[0_4px_4px_0px_rgba(0,0,0,0.25)] bg-[#FFFFFF] rounded-tl-[8px] rounded-tr-[24px] rounded-br-[24px] rounded-bl-[24px] flex flex-col justify-center items-center">
                          <div className="flex gap-3 px-4 my-5 w-[100%] text-black font-semibold text-sm flex-1">
                            <div className="leading-relaxed">
                              <div className="prose max-w-none font-hanken font-medium text-[15px] leading-[28px] text-[#4D4646]/80 overflow-x-auto">
                                {!chatbotOutput[index]
                                  ?.mandatory_data_present &&
                                  <ReactMarkdown
                                    remarkPlugins={[remarkGfm, remarkBreaks]}
                                    components={{
                                      p: ({ node, ...props }) => <p className="mb-2 last:mb-0 font-hanken font-medium text-[15px] leading-[28px] text-[#4D4646]/80" {...props} />,
                                      ul: ({ node, ...props }) => <ul className="list-disc ml-5 mb-2" {...props} />,
                                      ol: ({ node, ...props }) => <ol className="list-decimal ml-5 mb-2" {...props} />,
                                      li: ({ node, ...props }) => <li className="mb-1" {...props} />,
                                      table: ({ node, ...props }) => <table className="min-w-full border-collapse border border-gray-300 mb-2" {...props} />,
                                      th: ({ node, ...props }) => <th className="border border-gray-300 px-4 py-2 bg-gray-100 font-bold" {...props} />,
                                      td: ({ node, ...props }) => <td className="border border-gray-300 px-4 py-2" {...props} />
                                    }}
                                  >
                                    {chatbotOutput[index]?.Regional_Response || chatbotOutput[index]?.response || ""}
                                  </ReactMarkdown>}
                                {chatbotOutput[index]
                                  ?.mandatory_data_present && <div className="w-full bg-white shadow-lg border border-gray-200 rounded-xl overflow-hidden mt-2 mb-4">
                                    <div className="bg-[#8B1A4A] px-6 py-4">
                                      <h2 className="text-lg font-bold text-white m-0">
                                        {t.responseSummary || "Response Summary"}
                                      </h2>
                                    </div>
                                    <div className="p-6">
                                      <div className="mb-6 prose max-w-none text-gray-800 text-base leading-relaxed overflow-x-auto">
                                        <ReactMarkdown
                                          remarkPlugins={[remarkGfm, remarkBreaks]}
                                          components={{
                                            p: ({ node, ...props }) => <p className="mb-2 last:mb-0" {...props} />,
                                            ul: ({ node, ...props }) => <ul className="list-disc ml-5 mb-2" {...props} />,
                                            ol: ({ node, ...props }) => <ol className="list-decimal ml-5 mb-2" {...props} />,
                                            li: ({ node, ...props }) => <li className="mb-1" {...props} />,
                                            table: ({ node, ...props }) => <table className="min-w-full border-collapse border border-gray-300 mb-2" {...props} />,
                                            th: ({ node, ...props }) => <th className="border border-gray-300 px-4 py-2 bg-gray-100 font-bold" {...props} />,
                                            td: ({ node, ...props }) => <td className="border border-gray-300 px-4 py-2" {...props} />
                                          }}
                                        >
                                          {chatbotOutput[index]?.Regional_Response || chatbotOutput[index]?.response || ""}
                                        </ReactMarkdown>
                                      </div>
                                      <div className="mb-6">
                                        <h3 className="text-md font-semibold text-[#8B1A4A] mb-3 border-b border-gray-200 pb-2">
                                          {t.complaintDetails || "Complaint Details"}
                                        </h3>
                                        {chatbotOutput[index]?.selectedBucket && (
                                          <div className="rounded-lg overflow-hidden border border-gray-200">
                                            <table className="min-w-full border-collapse text-sm text-left">
                                              <tbody>
                                                {Object.entries(
                                                  chatbotOutput[index]
                                                    ?.selectedBucket
                                                ).map(([key, value], idx) => {
                                                  const displayKey = key.replace(/_/g, ' ');
                                                  const translatedKey = (key === "State" ? t.state : 
                                                    key === "District" ? t.district : 
                                                    key === "City" ? t.city : 
                                                    key === "Office_Area" ? t.officeArea : 
                                                    key === "Department" ? t.department : 
                                                    key === "Category" ? t.category : 
                                                    key === "Sub-Category" ? t.subCategory : displayKey);
                                                  return (
                                                  <tr
                                                    key={idx}
                                                    className="border-b border-gray-100 last:border-0 hover:bg-[#FDF0F4] transition-colors"
                                                  >
                                                    <td className="w-1/3 bg-gray-50 px-5 py-3 font-medium text-gray-700 border-r border-gray-100">
                                                      {translatedKey}
                                                    </td>
                                                    <td className="w-2/3 px-5 py-3 text-gray-800 bg-white">
                                                      {typeof value === "object"
                                                        ? JSON.stringify(value)
                                                        : value}
                                                    </td>
                                                  </tr>
                                                )})}
                                              </tbody>
                                            </table>
                                          </div>
                                        )}
                                      </div>
                                      <button
                                        className="mb-4 px-4 py-2.5 bg-white border border-[#8B1A4A] text-[#8B1A4A] font-medium rounded-lg shadow-sm hover:bg-gray-50 focus:outline-none transition-colors"
                                        onClick={() => togglePopup(index)}
                                      >
                                        {t.changeCategory || "Change Complaint Category"}
                                      </button>
                                      <div className="mb-4">
                                        <p className="text-gray-800 font-medium">
                                          {chatbotOutput[index]?.user_experience}
                                        </p>
                                      </div>
                                      <div className="flex gap-x-3 items-center">
                                        {!chatbotOutput[index]?.userFeedback && (
                                          <>
                                            <button
                                              className="px-4 py-2 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg shadow-sm hover:bg-blue-100 focus:outline-none transition-colors font-medium"
                                              onClick={() =>
                                                handleLikeDislike(index, "Liked")
                                              }
                                            >
                                              👍 {t.like || "Like"}
                                            </button>
                                            <button
                                              className="px-4 py-2 bg-red-50 text-red-700 border border-red-200 rounded-lg shadow-sm hover:bg-red-100 focus:outline-none transition-colors font-medium"
                                              onClick={() =>
                                                handleLikeDislike(
                                                  index,
                                                  "Disliked"
                                                )
                                              }
                                            >
                                              👎 {t.dislike || "Dislike"}
                                            </button>
                                          </>
                                        )}
                                        {chatbotOutput[index]?.userFeedback && (
                                          <button
                                            className={`px-4 py-2 text-white font-medium rounded-lg shadow-sm focus:outline-none ${chatbotOutput[index]
                                              ?.userFeedback === "Liked" ? "bg-blue-600 hover:bg-blue-700" :
                                              "bg-red-600 hover:bg-red-700"
                                              }`}
                                          >
                                            {chatbotOutput[index]
                                              ?.userFeedback === "Liked"
                                              ? `👍 ${t.like || "Like"}`
                                              : `👎 ${t.dislike || "Dislike"}`}
                                          </button>
                                        )}

                                        <button
                                          className="px-6 py-2 bg-[#8B1A4A] text-white font-medium rounded-lg shadow-sm hover:bg-[#6D1535] focus:outline-none transition-colors"
                                          onClick={() => handleSubmit(index)}
                                        >
                                          {t.submit || "Submit"}
                                        </button>
                                      </div>
                                    </div>              </div>}
                                {chatbotOutput[index]?.isFileUpload && chatbotOutput[index]?.summary && (
                                  <div className="mt-3 mb-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                    <h4 className="text-sm font-bold text-blue-800 mb-1">{t.summary || "Summary"}</h4>
                                    <p className="text-sm text-blue-900 whitespace-pre-line">
                                      {chatbotOutput[index]?.summary}
                                    </p>
                                  </div>
                                )}
                                {audioLoading && (
                                  <div className="my-2 flex gap-x-2 items-center">
                                    <ThreeDotsLoader className="animate-pulse" />
                                  </div>
                                )}
                                {chatbotOutput[index]?.audio_base64 && (
                                  <div className="mt-2 flex items-center gap-2">
                                    <AudioPlayer
                                      src={chatbotOutput[index]?.audio_url || `data:audio/mp3;base64,${chatbotOutput[index]?.audio_base64}`}
                                      duration={chatbotOutput[index]?.audio_duration || 0}
                                    />
                                    <span className="text-xs text-gray-400">🔊 {t.voiceResponse || "Voice response"}</span>
                                  </div>
                                )}
                              </div>
                              <div className="mt-3 flex gap-x-1 ">
                                <CopyToClipboard
                                  text={chatbotOutput[index]?.regional_text}
                                  onCopy={() =>
                                    handleCopy(
                                      chatbotOutput[index]?.regional_text
                                    )
                                  }
                                >
                                  <svg
                                    className="h-5 w-5 cursor-pointer"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    strokeWidth="2"
                                      stroke="#9c2942"
                                    fill="none"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  >
                                    {" "}
                                    <path
                                      stroke="none"
                                      d="M0 0h24v24H0z"
                                    />{" "}
                                    <rect
                                      x="8"
                                      y="8"
                                      width="12"
                                      height="12"
                                      rx="2"
                                    />{" "}
                                    <path d="M16 8v-2a2 2 0 0 0 -2 -2h-8a2 2 0 0 0 -2 2v8a2 2 0 0 0 2 2h2" />
                                  </svg>
                                </CopyToClipboard>

                                {index === chatbotInput.length - 1 && (
                                  <svg
                                    className="h-5 w-5 cursor-pointer"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                      stroke="#9c2942"
                                    onClick={handleRegenerate}
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                    />
                                  </svg>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {!chatbotOutput[index] && loading && (
                      <div className="flex gap-x-3 text-left mx-5 my-5 max-w-[80%] ">
                        <div className="relative flex shrink-0 items-center justify-center rounded-[16px] w-[44px] h-[44px] border-[2px] border-white bg-gradient-to-br from-[#9c2942] to-[#4a0e1e]">
                          <img
                            src={botLogo}
                            alt="bot logo"
                            className="w-[24px] h-[24px] invert"
                          />
                        </div>
                        <div className="flex flex-col items-center justify-center opacity-70 px-3 pt-3 pb-2 bg-white text-black text-sm font-semibold rounded-r-lg rounded-bl-lg shadow-lg">
                          <p>Almost there... your answer is being generated.</p>
                          <ThreeDotsLoader className="animate-pulse" />
                        </div>
                      </div>
                    )}
                  </>
                );
              })}
          </div>
        </div>
        <div className="absolute bottom-0 left-[20px] right-[20px] mb-[16px] z-10">
          <div className="flex items-center gap-[10px] bg-white rounded-full shadow-[0_10px_30px_rgba(74,14,30,0.08)] p-[8px_8px_8px_18px] pointer-events-auto w-full">
            <button
              onClick={handleMicClick}
              className={`w-[38px] h-[38px] rounded-full flex items-center justify-center shrink-0 transition-all ${isListening ? 'bg-[#fde3e1] text-[#e0433a] animate-pulse shadow-[0_0_0_4px_rgba(224,67,58,0.2)]' : 'text-[#9c2942] hover:bg-gray-100'}`}
            >
              {isListening ? (
                <SettingsVoiceIcon sx={{ width: 20, height: 20 }} />
              ) : (
                <i className="fa-solid fa-microphone text-[15px]"></i>
              )}
            </button>

            <button
              onClick={handleButtonClick}
              className="w-[38px] h-[38px] rounded-full flex items-center justify-center shrink-0 text-[#9c2942] hover:bg-gray-100 transition-colors"
            >
              <i className="fa-solid fa-paperclip text-[15px]"></i>
            </button>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFileChange}
            />

            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute bottom-full mb-2 left-[100px] z-40 max-w-full bg-white shadow-md border rounded-lg">
                <ul className="flex flex-col py-2">
                  {suggestions.map((suggestion, index) => (
                    <li
                      key={index}
                      className="px-4 py-1.5 hover:bg-gray-100 cursor-pointer flex justify-between items-center"
                      onClick={() => handleTranslationClick(suggestion)}
                    >
                      <span>{suggestion}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <input
              placeholder={
                Translation[selectedLanguageCode]?.searchPlaceholder ||
                "Type your query here or press mic for audio communication"
              }
              value={searchText}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              className="flex-1 bg-transparent border-none outline-none text-[14px] px-2 text-[#333333] placeholder-[#90A1B9]"
              style={{ fontFamily: 'inherit' }}
            />

            <button
              className={`w-[42px] h-[42px] rounded-full bg-[#9c2942] text-white flex items-center justify-center shrink-0 shadow-[0_4px_12px_rgba(156,41,66,0.3)] transition-opacity ${loading ? "opacity-50" : "hover:opacity-90"}`}
              onClick={handleSearch}
              disabled={loading || !searchText}
            >
              <i className="fa-solid fa-paper-plane text-[15px]"></i>
            </button>
          </div>
        </div>
        {isListOpen && (
          <div className="fixed inset-0 z-30 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-lg p-6 w-[95vw] sm:w-[80vw] max-w-2xl h-[80%] overflow-auto ">
              <h2 className="text-lg font-bold mb-4">Select a Table</h2>
              <ul>
                {Object.entries(
                  chatbotOutput[outputIndex]?.grievance_bucket
                ).map(([key, value], index) => (
                  <li
                    key={index}
                    className="py-2 px-4 hover:bg-gray-200 cursor-pointer rounded"
                    onClick={() => handleSelectedBucket(value)}
                  >
                    {/* {key} */}
                    <table className="min-w-full mt-2 text-sm border-collapse border border-gray-300">
                      <tbody>
                        {Object.entries(value).map(([key, value], index) => (
                          <tr
                            key={index}
                            className={
                              index % 2 === 0 ? "bg-[#d1d8dc]" : "bg-[white]"
                            }
                          >
                            <td className="border border-gray-400 px-4 py-2 font-medium text-gray-800 w-[30%] ">
                              {key}
                            </td>
                            <td className="border border-gray-400 px-4 py-2 text-gray-800 w-[70%] ">
                              {typeof value === "object"
                                ? JSON.stringify(value)
                                : value}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </li>
                ))}
              </ul>
              <button
                className="mt-4 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                onClick={togglePopup}
              >
                Close
              </button>
            </div>
          </div>
        )}
        {isPopupOpen && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white p-6 rounded-lg shadow-lg w-[90vw] max-w-sm">
              <h2 className="text-lg font-semibold mb-4">User Feedback</h2>

              <label htmlFor="feedback" className="block mb-2 text-gray-700">
                Your Feedback
              </label>
              <textarea
                id="feedback"
                value={feedback}
                onChange={handleFeedbackChange}
                rows="4"
                className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              ></textarea>

              <div className="flex justify-end mt-4">
                <button
                  onClick={closePopup}
                  className="px-4 py-2 text-white bg-red-500 rounded-lg hover:bg-red-600"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {fileLoading && <ScreenLoader />}
      {submitLoading && <ScreenLoader />}
    </>
  );
};

export default GrievanceBot;
